import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Factory, LogOut, Loader2, Plus, Pencil, Trash2, X, Save, Cog, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { authApi, clearAuth, getToken, formatApiErrorDetail } from "../lib/authApi";
import { ProcessChips } from "./PartnerRegister";
import { PartnerOrdersSection } from "../components/orders/PartnerOrdersSection";
import { PartnerPerformance } from "../components/analytics/PartnerPerformance";

const emptyMachine = { name: "", machine_type: "", process: "", capacity: "", quantity: "1", notes: "" };

const MachineForm = ({ processOptions, initial, onDone, onCancel }) => {
  const [form, setForm] = useState(initial ? { ...initial, quantity: String(initial.quantity) } : emptyMachine);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Machine name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, quantity: parseInt(form.quantity, 10) || 1 };
      const { data } = initial
        ? await authApi.patch(`/partner/machines/${initial.id}`, payload)
        : await authApi.post("/partner/machines", payload);
      toast.success(initial ? "Machine updated." : "Machine added.");
      onDone(data, !!initial);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 rounded-xl bg-muted/40 border border-border space-y-4" data-testid="machine-form">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold font-heading text-sm">{initial ? "Edit Machine" : "Add Machine"}</h4>
        <button onClick={onCancel} className="text-muted-foreground hover:text-foreground" data-testid="machine-form-close">
          <X className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Machine Name *</Label>
          <Input value={form.name} onChange={set("name")} placeholder="e.g. Haas VF-2" data-testid="machine-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Type</Label>
          <Input value={form.machine_type} onChange={set("machine_type")} placeholder="e.g. 3-Axis VMC" data-testid="machine-type-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Process</Label>
          <Select value={form.process} onValueChange={(v) => setForm({ ...form, process: v })}>
            <SelectTrigger data-testid="machine-process-select"><SelectValue placeholder="Link to process" /></SelectTrigger>
            <SelectContent>
              {processOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Capacity</Label>
          <Input value={form.capacity} onChange={set("capacity")} placeholder="e.g. 760×406×508mm" data-testid="machine-capacity-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Quantity</Label>
          <Input type="number" min="1" value={form.quantity} onChange={set("quantity")} data-testid="machine-qty-input" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Notes</Label>
        <Textarea rows={2} value={form.notes} onChange={set("notes")} placeholder="Tolerances, tooling, special capabilities..." data-testid="machine-notes-input" />
      </div>
      <Button size="sm" disabled={saving} onClick={save} className="rounded-full px-6" data-testid="machine-save-btn">
        {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}
        {initial ? "Save Changes" : "Add Machine"}
      </Button>
    </div>
  );
};

export default function PartnerPortal() {
  const [user, setUserState] = useState(null);
  const [machines, setMachines] = useState([]);
  const [processOptions, setProcessOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [machineFormOpen, setMachineFormOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) {
      navigate("/partner/login");
      return;
    }
    (async () => {
      try {
        const me = await authApi.get("/auth/me");
        if (me.data.role !== "partner") {
          navigate(me.data.role === "admin" ? "/admin" : "/portal");
          return;
        }
        const [prof, mach, cfg, ord] = await Promise.all([
          authApi.get("/partner/profile"),
          authApi.get("/partner/machines"),
          authApi.get("/library/pricing-config"),
          authApi.get("/partner/orders"),
        ]);
        setOrders(ord.data);
        setUserState(prof.data);
        setMachines(mach.data);
        setProcessOptions(cfg.data.processes.map((p) => p.name));
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          toast.error(formatApiErrorDetail(err.response?.data?.detail));
          clearAuth();
          navigate("/partner/login");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const logout = () => {
    clearAuth();
    navigate("/partner/login");
  };

  const startProfileEdit = () => setProfileForm({
    company_name: user.company_name || "", name: user.name || "", phone: user.phone || "",
    city: user.city || "", state: user.state || "", processes: user.processes || [],
    gst_number: user.gst_number || "", certifications: (user.certifications || []).join(", "),
    factory_size: user.factory_size || "",
    years_in_business: user.years_in_business == null ? "" : String(user.years_in_business),
  });

  const saveProfile = async () => {
    if (!profileForm.company_name.trim() || !profileForm.name.trim()) {
      toast.error("Company name and contact name are required.");
      return;
    }
    setSavingProfile(true);
    try {
      const { data } = await authApi.patch("/partner/profile", {
        ...profileForm,
        certifications: profileForm.certifications.split(",").map((x) => x.trim()).filter(Boolean),
        years_in_business: profileForm.years_in_business === "" ? null : parseInt(profileForm.years_in_business, 10),
      });
      setUserState(data);
      setProfileForm(null);
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const removeMachine = async (m) => {
    if (!window.confirm(`Delete ${m.name}?`)) return;
    setDeleting(m.id);
    try {
      await authApi.delete(`/partner/machines/${m.id}`);
      setMachines(machines.filter((x) => x.id !== m.id));
      toast.success("Machine deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(null);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="partner-portal-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" strokeWidth={1.5} />
      </div>
    );
  }

  const profileRows = [
    ["Contact", user.name], ["Email", user.email], ["Phone", user.phone],
    ["Location", [user.city, user.state].filter(Boolean).join(", ")],
    ["GST", user.gst_number], ["Factory Size", user.factory_size],
    ["Years in Business", user.years_in_business],
  ];

  return (
    <div className="min-h-screen" data-testid="partner-portal">
      <section className="py-12 border-b border-border bg-muted/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Factory className="h-5 w-5 text-primary" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-medium font-heading tracking-tight" data-testid="partner-portal-company">{user.company_name}</h1>
              <p className="text-sm text-muted-foreground">Partner Portal · <span className="text-emerald-600 dark:text-emerald-400 font-semibold uppercase text-xs">{user.partner_status}</span></p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={logout} className="rounded-full" data-testid="partner-logout-btn">
            <LogOut className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Sign Out
          </Button>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <PartnerPerformance />
          <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "profile"}>
            <TabsList className="w-full sm:w-auto justify-start overflow-x-auto scrollbar-none tab-strip-fade h-auto" data-testid="partner-tabs">
              <TabsTrigger value="profile" data-testid="partner-tab-profile">Profile & Capabilities</TabsTrigger>
              <TabsTrigger value="machines" data-testid="partner-tab-machines"><Cog className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Machines ({machines.length})</TabsTrigger>
              <TabsTrigger value="orders" data-testid="partner-tab-orders"><Package className="h-4 w-4 mr-1.5" strokeWidth={1.5} />Work Orders ({orders.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="orders" className="mt-6">
              <PartnerOrdersSection orders={orders} setOrders={setOrders} />
            </TabsContent>

            <TabsContent value="profile" className="mt-6">
              {!profileForm ? (
                <div className="p-8 rounded-xl bg-card border border-border" data-testid="partner-profile-view">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-semibold font-heading text-lg">Company Profile</h3>
                    <Button size="sm" variant="outline" onClick={startProfileEdit} className="rounded-full" data-testid="partner-profile-edit-btn">
                      <Pencil className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Edit
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                    {profileRows.map(([label, value]) => (
                      <div key={label}>
                        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-mono-tech">{label}</p>
                        <p className="mt-1 text-sm font-medium">{value || "—"}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 pt-5 border-t border-border">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-mono-tech mb-2">Processes Offered</p>
                    <div className="flex flex-wrap gap-2">
                      {(user.processes || []).length ? user.processes.map((p) => <Badge key={p} variant="secondary">{p}</Badge>) : <span className="text-sm text-muted-foreground">None selected yet</span>}
                    </div>
                    <p className="mt-4 text-xs font-bold uppercase tracking-wider text-muted-foreground font-mono-tech mb-2">Certifications</p>
                    <div className="flex flex-wrap gap-2">
                      {(user.certifications || []).length ? user.certifications.map((c) => <Badge key={c} variant="outline">{c}</Badge>) : <span className="text-sm text-muted-foreground">—</span>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-8 rounded-xl bg-card border border-border space-y-4" data-testid="partner-profile-form">
                  <h3 className="font-semibold font-heading text-lg">Edit Profile</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[["company_name", "Company Name *"], ["name", "Contact Person *"], ["phone", "Phone"], ["city", "City"], ["state", "State"], ["gst_number", "GST Number"], ["factory_size", "Factory Size"], ["years_in_business", "Years in Business"]].map(([k, label]) => (
                      <div key={k} className="space-y-1.5">
                        <Label className="text-xs">{label}</Label>
                        <Input value={profileForm[k]} type={k === "years_in_business" ? "number" : "text"}
                          onChange={(e) => setProfileForm({ ...profileForm, [k]: e.target.value })}
                          data-testid={`partner-profile-${k}-input`} />
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Processes Offered</Label>
                    <ProcessChips options={processOptions} selected={profileForm.processes}
                      onToggle={(p) => setProfileForm({ ...profileForm, processes: profileForm.processes.includes(p) ? profileForm.processes.filter((x) => x !== p) : [...profileForm.processes, p] })}
                      testPrefix="partner-profile-process" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Certifications (comma-separated)</Label>
                    <Input value={profileForm.certifications} onChange={(e) => setProfileForm({ ...profileForm, certifications: e.target.value })} data-testid="partner-profile-certs-input" />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button size="sm" disabled={savingProfile} onClick={saveProfile} className="rounded-full px-6" data-testid="partner-profile-save-btn">
                      {savingProfile ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setProfileForm(null)} className="rounded-full" data-testid="partner-profile-cancel-btn">Cancel</Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="machines" className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Your machines power capability matching for incoming orders.</p>
                {!machineFormOpen && !editingMachine && (
                  <Button size="sm" onClick={() => setMachineFormOpen(true)} className="rounded-full" data-testid="machine-add-btn">
                    <Plus className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add Machine
                  </Button>
                )}
              </div>
              {(machineFormOpen || editingMachine) && (
                <MachineForm processOptions={processOptions} initial={editingMachine}
                  onDone={(m, wasEdit) => {
                    setMachines(wasEdit ? machines.map((x) => (x.id === m.id ? m : x)) : [m, ...machines]);
                    setMachineFormOpen(false);
                    setEditingMachine(null);
                  }}
                  onCancel={() => { setMachineFormOpen(false); setEditingMachine(null); }} />
              )}
              {machines.length === 0 && !machineFormOpen && (
                <div className="p-10 rounded-xl bg-card border border-border text-center" data-testid="machines-empty">
                  <Cog className="h-8 w-8 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="font-semibold font-heading">No machines added</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Add your machines so we can match you with the right orders.</p>
                </div>
              )}
              {machines.map((m) => (
                <div key={m.id} className="flex flex-wrap items-center gap-3 px-5 py-4 rounded-xl bg-card border border-border" data-testid={`machine-card-${m.id}`}>
                  <span className="font-semibold font-heading">{m.name}</span>
                  {m.machine_type && <Badge variant="secondary">{m.machine_type}</Badge>}
                  {m.process && <Badge variant="outline">{m.process}</Badge>}
                  {m.capacity && <span className="text-xs text-muted-foreground">{m.capacity}</span>}
                  <span className="text-xs text-muted-foreground">× {m.quantity}</span>
                  {m.notes && <span className="text-xs text-muted-foreground">— {m.notes}</span>}
                  <span className="ml-auto flex gap-2">
                    <button onClick={() => { setEditingMachine(m); setMachineFormOpen(false); }} className="p-2 -m-1 text-muted-foreground hover:text-primary transition-colors duration-200" data-testid={`machine-edit-btn-${m.id}`}>
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                    <button onClick={() => removeMachine(m)} disabled={deleting === m.id} className="p-2 -m-1 text-muted-foreground hover:text-destructive transition-colors duration-200" data-testid={`machine-delete-btn-${m.id}`}>
                      {deleting === m.id ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Trash2 className="h-4 w-4" strokeWidth={1.5} />}
                    </button>
                  </span>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
